import { useMutation, useQuery } from "@apollo/client";
import ArrowForward from "@mui/icons-material/ArrowForward";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { LoadingButton } from "@mui/lab";
import { Alert, Box, Button, Skeleton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ResolutionStatusChip } from "components/ResolutionStatusChip";
import { RightDrawerShell } from "components/RightDrawerShell/RightDrawerShell";
import { StatusChip } from "components/StatusChip";
import { LQG_THRESHOLD_ACUTE_HAZARDOUS_LB } from "encamp-shared/src/constants/wasteGeneratorStatusThresholds";
import { stateDoesNotFollowFederalGeneratorStatus } from "encamp-shared/src/waste/constants";
import { gql } from "generated-graphql";
import {
  GeneratorStatusExceedanceDirection,
  GeneratorStatusExceedanceRiskLevel,
  RcraInfoFederalWasteGeneratorStatus,
  ResolutionStatus,
  StateRegulatoryStatus,
} from "generated-graphql/graphql";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  CreateGeneratorStatusChangeSubmissionsDialog,
  GeneratorStatusChangeFacility,
} from "./CreateGeneratorStatusChangeSubmissionsDialog";
import { DRAWER_PARAM, toDrawerParam } from "./drawerDeepLink";
import { drawerSectionSx } from "./DrawerSection";
import { ExceedanceEvidence } from "./ExceedanceEvidence";
import { EXCEEDANCE_REFETCH, ExceedanceKey } from "./exceedanceKeys";
import { FacilityStatusContext, FacilityStatusIndicator } from "./FacilityStatusIndicator";
import { FindingStatusComparison } from "./FindingStatusComparison";
import {
  canFileNotification,
  determinationFactors,
  formatLbs,
  isAcuteDriven,
} from "./generatorStatusDisplay";
import { resolutionMethodLabel } from "./manifestCorrectionDisplay";
import { MarkResolvedDialog } from "./MarkResolvedDialog";
import { deadlineBasis, deadlineChip, stageWaiting } from "./notificationDeadline";
import { formatPeriodSpan } from "./periodFormat";
import { ComparisonForDisplay, PriorityComparisonSummary } from "./PriorityComparisonSummary";
import { ReviewFollowUp } from "./ReviewFollowUp";
import { stageLabel } from "./stages";

type LinkedSubmissionSummary = {
  id: string;
  title: string;
  submissionType?: string;
  status: string;
  dueDate?: string | null;
};

export type ExceedanceForDrawer = FacilityStatusContext & {
  facilityId: string;
  tenantId: string;
  facilityName: string;
  organization: string;
  state: string | null;
  source: string;
  periodStart: string;
  /** Latest month of the status run; equals periodStart for one month. */
  lastPeriodStart?: string | null;
  reportedFederalStatus?: RcraInfoFederalWasteGeneratorStatus | null;
  calculatedFederalStatus?: RcraInfoFederalWasteGeneratorStatus | null;
  /** State-axis snapshots; populated only for states with their own generator tiers. */
  reportedStateStatus?: StateRegulatoryStatus | null;
  reportedStateStatusLabel?: string | null;
  stateComparisonPeriodStart?: string | null;
  calculatedStateStatus?: StateRegulatoryStatus | null;
  /** Non-null when the state axis mismatches (reported vs calculated). */
  stateDirection?: GeneratorStatusExceedanceDirection | null;
  changeExplanation: string;
  resolutionStatus: ResolutionStatus;
  direction?: GeneratorStatusExceedanceDirection | null;
  sustainedMonths?: number | null;
  priorityComparison?: ComparisonForDisplay | null;
  /** Detected mismatching months folded into the run; null until the next sweep. */
  mismatchMonths?: number | null;
  /** Which months mismatched; empty on rows predating the list. */
  mismatchPeriodStarts?: readonly string[] | null;
  /** Derived compliance-risk signal; UPWARD rows only. */
  riskLevel?: GeneratorStatusExceedanceRiskLevel | null;
  detectedAt?: string | null;
  validatedAt?: string | null;
  staffAssigneeName?: string | null;
  openSubmission?: LinkedSubmissionSummary | null;
  linkedSubmission?: LinkedSubmissionSummary | null;
  manifestCorrectionCount?: number | null;
  deadlineTier?: number | null;
  deadlineWindowDays?: number | null;
  deadlineDaysRemaining?: number | null;
  awaitingCorrectionSince?: string | null;
  awaitingEpaUpdateSince?: string | null;
};

const DETAIL_QUERY = gql(`
  query GeneratorStatusExceedanceDetail($facilityId: ID!, $source: String!, $periodStart: Date!) {
    generatorStatusExceedanceDetail(facilityId: $facilityId, source: $source, periodStart: $periodStart) {
      facilityId
      source
      periodStart
      epaIds
      federalThresholds {
        status
        range
        condition
      }
      stateThresholds {
        status
        range
        condition
      }
      stateCountsFullTotal
      determinationFactors
      resolution {
        id
        status
        resolutionType
        note
        resolvedAt
        reportedFederalStatusSnapshot
        calculatedFederalStatusSnapshot
        reportedStateStatusSnapshot
        calculatedStateStatusSnapshot
        direction
        stateDirection
      }
      events {
        id
        eventType
        status
        resolutionType
        note
        explanation {
          basis
          message
        }
        comparison
        actorId
        actorName
        createdAt
      }
      sources {
        source
        federalStatus
        totalHazardousLbs
        acuteLbs
        episodicHazardousExemptLbs
        episodicAcuteExemptLbs
        episodicStateRegulatedExemptLbs
      }
      calculatedMonths {
        periodStart
        reportedFederalStatus
        reportedStateStatus
        reportedReceiveDate
        federalStatus
        stateStatus
        totalHazardousLbs
        acuteLbs
        stateRegulatedLbs
        episodicHazardousExemptLbs
        episodicAcuteExemptLbs
        episodicStateRegulatedExemptLbs
        mismatch
        inRun
      }
      relatedDiscrepancies {
        periodStart
        lastPeriodStart
        mismatchPeriodStarts
        direction
        stateDirection
        reportedFederalStatus
        calculatedFederalStatus
        reportedStateStatus
        calculatedStateStatus
      }
      wadmByMonth {
        periodStart
        totalWeightLbs
        itemCount
        wadmGeneratorStatus
      }
      manifestCorrections {
        staffReported
        id
        processedManifestId
        manifestTrackingNumber
        shippedDate
        wasteLineNumber
        field
        status
        resolutionPath
        eManifestValue
        vendorValue
        correctValue
        resolvedAt
        resolvedByName
      }
      previousManifestCorrections {
        staffReported
        id
        processedManifestId
        manifestTrackingNumber
        shippedDate
        wasteLineNumber
        field
        status
        resolutionPath
        eManifestValue
        vendorValue
        correctValue
        resolvedAt
        resolvedByName
      }
      rcraInfoCatchUp {
        reportedFederalStatus
        reportedStateStatus
        caughtUp
      }
      timing {
        deadlineTier
        deadlineWindowDays
        deadlineDaysRemaining
        awaitingCorrectionSince
        awaitingEpaUpdateSince
      }
    }
  }
`);

const DISMISS_MUTATION = gql(`
  mutation DismissGeneratorStatusExceedances(
    $exceedances: [GeneratorStatusExceedanceKeyInput!]!
    $reason: String!
  ) {
    dismissGeneratorStatusExceedances(exceedances: $exceedances, reason: $reason) {
      id
      status
    }
  }
`);

const REOPEN_MUTATION = gql(`
  mutation ReopenGeneratorStatusExceedances(
    $exceedances: [GeneratorStatusExceedanceKeyInput!]!
    $note: String
  ) {
    reopenGeneratorStatusExceedances(exceedances: $exceedances, note: $note) {
      id
      status
    }
  }
`);

const VALIDATE_MUTATION = gql(`
  mutation ValidateGeneratorStatusExceedance($exceedances: [GeneratorStatusExceedanceKeyInput!]!) {
    validateGeneratorStatusExceedances(exceedances: $exceedances) {
      id
      status
    }
  }
`);

const UNVALIDATE_MUTATION = gql(`
  mutation UnvalidateGeneratorStatusExceedance($exceedances: [GeneratorStatusExceedanceKeyInput!]!) {
    unvalidateGeneratorStatusExceedances(exceedances: $exceedances) {
      id
      status
    }
  }
`);

function stageRole(
  status: ResolutionStatus,
  validated: boolean,
  awaitingEpaUpdate: boolean
): "warning" | "info" | undefined {
  if (status === ResolutionStatus.Unresolved) return validated ? "warning" : undefined;
  return status === ResolutionStatus.Resolved && awaitingEpaUpdate ? "info" : undefined;
}

type Props = {
  exceedance: ExceedanceForDrawer | null;
  /** Whether a deep-linked row is still loading. */
  loading?: boolean;
  onClose: () => void;
  /** Called after a resolution action so the caller can refetch + clear state. */
  onResolved: () => void;
};

function useExceedanceDetail(exceedance: ExceedanceForDrawer | null) {
  return useQuery(DETAIL_QUERY, {
    variables: exceedance
      ? {
          facilityId: exceedance.facilityId,
          source: exceedance.source,
          periodStart: exceedance.periodStart,
        }
      : { facilityId: "", source: "", periodStart: "" },
    skip: !exceedance,
    fetchPolicy: "cache-and-network",
  });
}

export function ExceedanceActionDrawer({
  exceedance,
  loading = false,
  onClose,
  onResolved,
}: Props) {
  const open = exceedance !== null || loading;
  const [notePanel, setNotePanel] = useState<"dismiss" | "reset" | null>(null);
  const [noteText, setNoteText] = useState("");
  const [fileDialogOpen, setFileDialogOpen] = useState(false);
  const [markResolvedOpen, setMarkResolvedOpen] = useState(false);

  const { data, loading: detailLoading, error: detailError } = useExceedanceDetail(exceedance);

  const [dismiss, { loading: dismissing }] = useMutation(DISMISS_MUTATION, {
    refetchQueries: EXCEEDANCE_REFETCH,
    awaitRefetchQueries: true,
  });
  const [reopen, { loading: reopening }] = useMutation(REOPEN_MUTATION, {
    refetchQueries: EXCEEDANCE_REFETCH,
    awaitRefetchQueries: true,
  });

  const [validate, { loading: validating }] = useMutation(VALIDATE_MUTATION, {
    refetchQueries: EXCEEDANCE_REFETCH,
    awaitRefetchQueries: true,
  });
  const [unvalidate, { loading: unvalidating }] = useMutation(UNVALIDATE_MUTATION, {
    refetchQueries: EXCEEDANCE_REFETCH,
    awaitRefetchQueries: true,
  });

  const alerts = useAlerts();

  const busy = dismissing || reopening || validating || unvalidating;

  // Validate keeps the drawer open (you often validate, then file). The parent passes
  // a snapshot, so we overlay the changed field locally and refetch the grid behind the
  // scenes; the overlay resets when a different row opens.
  const [overlay, setOverlay] = useState<{
    validatedAt?: string | null;
  }>({});
  useEffect(() => {
    setOverlay({});
  }, [exceedance?.facilityId, exceedance?.source, exceedance?.periodStart]);
  const effectiveValidatedAt =
    "validatedAt" in overlay ? overlay.validatedAt : (exceedance?.validatedAt ?? null);

  const exceedanceKey = useMemo<ExceedanceKey | null>(
    () =>
      exceedance
        ? {
            facilityId: exceedance.facilityId,
            source: exceedance.source,
            periodStart: exceedance.periodStart,
            reportedFederalStatus: exceedance.reportedFederalStatus ?? null,
            calculatedFederalStatus: exceedance.calculatedFederalStatus ?? null,
          }
        : null,
    [exceedance]
  );

  const detail = data?.generatorStatusExceedanceDetail;
  // The detail query refetches after every mutation, so actions that keep the drawer
  // open (Mark fixed) show the current status instead of the parent's snapshot.
  const effectiveStatus = detail?.resolution?.status ?? exceedance?.resolutionStatus;
  // How the row was resolved, in the same words the CSV export uses.
  const resolutionMethod = resolutionMethodLabel(detail?.resolution?.resolutionType);
  const federalFactors = determinationFactors(detail?.determinationFactors, "federal");

  // Builds a same-page href that swaps the drawer to a related exceedance via its
  // deep-link param, preserving every other query param (omnisearch filters etc.).
  // The dashboard loads the new row and swaps the drawer in place.
  const [pageSearchParams] = useSearchParams();
  const siblingDrawerSearch = (siblingPeriodStart: string): string => {
    const next = new URLSearchParams(pageSearchParams);
    if (exceedance) {
      next.set(
        DRAWER_PARAM,
        toDrawerParam({
          facilityId: exceedance.facilityId,
          source: exceedance.source,
          periodStart: siblingPeriodStart,
        })
      );
    }
    return `?${next.toString()}`;
  };
  // Acute-driven determination: the run's latest month is LQG on acute waste alone.
  // Called out prominently — a facility at a few hundred total lbs reads as
  // obviously-not-LQG unless the 1 kg acute rule is front and center.
  const factorAcuteLbs = federalFactors?.["Acute Waste (lbs)"] ?? null;
  const factorTotalLbs = federalFactors?.["Total Hazardous Waste (lbs)"] ?? null;
  const acuteDriven = isAcuteDriven(
    exceedance?.calculatedFederalStatus,
    factorAcuteLbs,
    factorTotalLbs
  );
  const showStateColumns =
    Boolean(exceedance?.stateDirection) ||
    stateDoesNotFollowFederalGeneratorStatus(exceedance?.state);

  const closeAndReset = () => {
    setNotePanel(null);
    setNoteText("");
    onClose();
  };

  const runDismiss = async () => {
    if (!exceedanceKey) return;
    try {
      await dismiss({ variables: { exceedances: [exceedanceKey], reason: noteText } });
      alerts.success("Dismissed.");
      setNotePanel(null);
      setNoteText("");
      onResolved();
    } catch (e) {
      alerts.error("Failed to dismiss.", e);
    }
  };

  // Reopen/reset back to UNRESOLVED — also unlinks any linked submission. The
  // optional reason is recorded on the audit event.
  const runReset = async () => {
    if (!exceedanceKey) return;
    try {
      await reopen({ variables: { exceedances: [exceedanceKey], note: noteText || undefined } });
      alerts.success(linkedOrOpenSubmission ? "Unlinked & reset to unresolved." : "Reopened.");
      setNotePanel(null);
      setNoteText("");
      onResolved();
    } catch (e) {
      alerts.error("Failed to reset.", e);
    }
  };

  // Validate (TPM) — marks ready for pickup; keeps the drawer open so you can then
  // file. Mirrors the bulk "Validate (mark ready for pickup)" action.
  const runValidate = async () => {
    if (!exceedanceKey) return;
    try {
      await validate({ variables: { exceedances: [exceedanceKey] } });
      alerts.success("Validated — ready for pickup.");
      setOverlay((o) => ({ ...o, validatedAt: new Date().toISOString() }));
    } catch (e) {
      alerts.error("Failed to validate.", e);
    }
  };

  // Un-validate (TPM) — undo a validation applied in error; the row drops back to
  // "Needs validation". Keeps the drawer open so the footer re-renders to the triage
  // fork, mirroring runValidate (which keeps it open to then file).
  const runUnvalidate = async () => {
    if (!exceedanceKey) return;
    try {
      await unvalidate({ variables: { exceedances: [exceedanceKey] } });
      alerts.success("Validation undone — back to needs validation.");
      setOverlay((o) => ({ ...o, validatedAt: null }));
    } catch (e) {
      alerts.error("Failed to undo the validation.", e);
    }
  };

  const isResolved =
    effectiveStatus === ResolutionStatus.Resolved || effectiveStatus === ResolutionStatus.Ignored;
  const isUnresolved = effectiveStatus === ResolutionStatus.Unresolved;
  const isValidated = isUnresolved && Boolean(effectiveValidatedAt);
  const linkedOrOpenSubmission = exceedance?.linkedSubmission ?? exceedance?.openSubmission ?? null;
  // A linked/open submission means the reset also unlinks it.
  const resetLabel = linkedOrOpenSubmission ? "Unlink & reset" : "Reopen";

  const facilityForFiling: GeneratorStatusChangeFacility[] = exceedance
    ? [
        {
          facilityId: exceedance.facilityId,
          tenantId: exceedance.tenantId,
          facilityName: exceedance.facilityName,
          organization: exceedance.organization,
          state: exceedance.state,
          periodStart: exceedance.periodStart,
        },
      ]
    : [];

  // Available actions follow the current review stage.
  const renderFooterActions = () => {
    if (notePanel) {
      return (
        <Stack spacing={1}>
          <TextField
            label={
              notePanel === "dismiss"
                ? "Reason for dismissal"
                : `Reason (optional)${linkedOrOpenSubmission ? " — this unlinks the submission" : ""}`
            }
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            size="small"
            multiline
            minRows={2}
            autoFocus
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={() => setNotePanel(null)} disabled={busy}>
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              onClick={notePanel === "dismiss" ? runDismiss : runReset}
              loading={notePanel === "dismiss" ? dismissing : reopening}
              disabled={busy || (notePanel === "dismiss" && !noteText.trim())}
            >
              {notePanel === "dismiss" ? "Dismiss" : "Reset to unresolved"}
            </LoadingButton>
          </Stack>
        </Stack>
      );
    }

    if (isResolved) {
      return (
        <Button variant="outlined" onClick={() => setNotePanel("reset")} disabled={busy}>
          Reopen
        </Button>
      );
    }

    // Needs validation — the TPM triage fork: one decision, two opposite outcomes.
    if (isUnresolved && !isValidated) {
      return (
        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary">
            Validate to mark ready for follow-up. Dismiss if no update is needed. Already corrected?
            Record it instead.
          </Typography>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              fullWidth
              variant="contained"
              onClick={runValidate}
              loading={validating}
              disabled={busy}
            >
              Validate
            </LoadingButton>
            <Button
              fullWidth
              variant="outlined"
              color="warning"
              onClick={() => setNotePanel("dismiss")}
              disabled={busy}
            >
              Dismiss
            </Button>
          </Stack>
          <Button
            size="small"
            endIcon={<ArrowForward />}
            sx={{ alignSelf: "flex-start", textTransform: "none" }}
            onClick={() => setMarkResolvedOpen(true)}
            disabled={busy}
          >
            Record a correction or link existing work
          </Button>
        </Stack>
      );
    }

    // Validated · Ready for Pickup — the forward action is filing; the rest are quiet escapes.
    if (isValidated) {
      return (
        <Stack spacing={1}>
          {exceedance && canFileNotification(exceedance) && (
            <Button variant="contained" onClick={() => setFileDialogOpen(true)} disabled={busy}>
              Create notification
            </Button>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button size="small" onClick={() => setMarkResolvedOpen(true)} disabled={busy}>
              Mark resolved
            </Button>
            <LoadingButton
              size="small"
              onClick={runUnvalidate}
              loading={unvalidating}
              disabled={busy}
            >
              Undo validation
            </LoadingButton>
            <Button
              size="small"
              color="warning"
              onClick={() => setNotePanel("dismiss")}
              disabled={busy}
            >
              Dismiss
            </Button>
          </Stack>
        </Stack>
      );
    }

    // In flight (PENDING) — tracking the linked submission.
    return (
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Button variant="outlined" onClick={() => setMarkResolvedOpen(true)} disabled={busy}>
          Mark resolved
        </Button>
        <Button variant="outlined" onClick={() => setNotePanel("reset")} disabled={busy}>
          {resetLabel}
        </Button>
      </Stack>
    );
  };

  // The row is a snapshot from the grid; the detail query refetches after every action.
  const current = exceedance ? { ...exceedance, ...detail?.timing } : null;
  const drawerStageLabel = current
    ? stageLabel({
        ...current,
        resolutionStatus: effectiveStatus ?? current.resolutionStatus,
        manifestCorrectionCount:
          detail?.manifestCorrections.length ?? current.manifestCorrectionCount,
        validatedAt: effectiveValidatedAt,
      })
    : "";
  const waiting = current ? stageWaiting(drawerStageLabel, current) : null;
  const isOpen =
    effectiveStatus === ResolutionStatus.Unresolved || effectiveStatus === ResolutionStatus.Pending;
  const deadline = current && isOpen ? deadlineChip(current) : null;

  const footer = exceedance ? (
    <Stack spacing={1.5} sx={{ pr: { xs: 1, sm: 2 } }}>
      {renderFooterActions()}
    </Stack>
  ) : null;

  return (
    <>
      <RightDrawerShell
        open={open}
        onClose={closeAndReset}
        width={{ xs: "95%", sm: 760, md: 880 }}
        // Freeze the page behind the drawer: its month history can scroll far
        // longer than the grid, and two live scrollbars side by side made it
        // easy to scroll the wrong surface.
        disableScrollLock={false}
        title={
          exceedance ? (
            <Link
              to={`/o/${exceedance.tenantId}/waste/facilities/${exceedance.facilityId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "inherit",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontWeight: 500,
              }}
            >
              {exceedance.facilityName}
              <OpenInNew fontSize="small" />
            </Link>
          ) : (
            <Skeleton width={220} />
          )
        }
        footer={footer}
      >
        {!exceedance && loading && (
          // The deep-linked row is still loading — sketch the drawer's
          // shape (status row, comparison block, months table) instead of a
          // dead pane.
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Skeleton variant="rounded" width={140} height={24} />
              <Skeleton width={180} />
            </Stack>
            <Skeleton width="70%" />
            <Skeleton variant="rounded" height={96} />
            <Skeleton variant="rounded" height={180} />
          </Stack>
        )}
        {exceedance && (
          <Stack spacing={2.5} sx={{ pr: { xs: 1, sm: 2 } }}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <ResolutionStatusChip
                  status={effectiveStatus ?? exceedance.resolutionStatus}
                  label={drawerStageLabel}
                  role={stageRole(
                    effectiveStatus ?? exceedance.resolutionStatus,
                    isValidated,
                    Boolean(current?.awaitingEpaUpdateSince)
                  )}
                />
                {waiting && (
                  <Typography variant="body2" color={waiting.overdue ? "error" : "text.secondary"}>
                    {waiting.label}
                  </Typography>
                )}
                {deadline && (
                  <Tooltip title={deadlineBasis(exceedance)}>
                    <span>
                      <StatusChip color={deadline.color} label={`Deadline: ${deadline.label}`} />
                    </span>
                  </Tooltip>
                )}
                {/* Compliance-risk badge: a sustained/recurring exceedance
                  uses the span between affected months, including gaps. */}
                {exceedance.riskLevel && (
                  <Tooltip title="Based on the span between affected months, including gaps. This is separate from time spent awaiting review.">
                    <span>
                      <StatusChip
                        color={
                          exceedance.riskLevel === GeneratorStatusExceedanceRiskLevel.High
                            ? "error"
                            : "warning"
                        }
                        label={
                          exceedance.riskLevel === GeneratorStatusExceedanceRiskLevel.High
                            ? "High risk"
                            : "Elevated risk"
                        }
                      />
                    </span>
                  </Tooltip>
                )}
                {/* The LQG determination rests on the 1 kg acute rule, not tonnage —
                  shown at the top so it can't be missed while triaging. */}
                {acuteDriven && (
                  <Tooltip
                    title={`≥ ${LQG_THRESHOLD_ACUTE_HAZARDOUS_LB} lbs (1 kg) of acutely hazardous waste in a month makes a facility LQG regardless of total weight.`}
                  >
                    <span>
                      <StatusChip color="error" label="Acute waste–driven" />
                    </span>
                  </Tooltip>
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {[
                  exceedance.organization,
                  exceedance.state ?? "—",
                  detail?.epaIds.length ? `EPA ID ${detail.epaIds.join(", ")}` : null,
                  formatPeriodSpan(exceedance),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            </Stack>

            <PriorityComparisonSummary comparison={exceedance.priorityComparison} />
            <FacilityStatusIndicator facility={exceedance} details />

            <Box sx={drawerSectionSx}>
              <FindingStatusComparison
                exceedance={exceedance}
                detail={detail}
                loading={detailLoading}
              />
              {acuteDriven && (
                <Alert severity="warning" variant="outlined" sx={{ mt: 1.5 }}>
                  The federal LQG calculation is driven by acute hazardous waste:{" "}
                  {formatLbs(factorAcuteLbs ?? 0)} lb, above the 1 kg (
                  {LQG_THRESHOLD_ACUTE_HAZARDOUS_LB} lb) threshold.
                  {factorTotalLbs != null &&
                    ` Total hazardous weight is ${formatLbs(factorTotalLbs)} lb.`}
                </Alert>
              )}
            </Box>

            {detailError && (
              <Alert severity="error">
                Calculation details and review history could not be loaded. Refresh the page to try
                again.
              </Alert>
            )}
            {!detail && detailLoading && <Skeleton variant="rounded" height={180} />}
            {detail && (
              <ExceedanceEvidence
                key={`${exceedance.facilityId}|${exceedance.source}|${exceedance.periodStart}`}
                detail={detail}
                exceedance={exceedance}
                showStateColumns={showStateColumns}
              />
            )}
            <ReviewFollowUp
              detail={detail}
              exceedance={exceedance}
              exceedanceKey={exceedanceKey}
              validatedAt={effectiveValidatedAt}
              resolutionMethod={resolutionMethod}
              loading={detailLoading}
              error={Boolean(detailError)}
              siblingDrawerSearch={siblingDrawerSearch}
            />
          </Stack>
        )}
      </RightDrawerShell>

      <CreateGeneratorStatusChangeSubmissionsDialog
        open={fileDialogOpen}
        onClose={() => setFileDialogOpen(false)}
        facilities={facilityForFiling}
        onSuccess={() => {
          setFileDialogOpen(false);
          onResolved();
        }}
      />

      {exceedanceKey && exceedance && (
        <MarkResolvedDialog
          open={markResolvedOpen}
          onClose={() => setMarkResolvedOpen(false)}
          exceedanceKey={exceedanceKey}
          facilityId={exceedance.facilityId}
          tenantId={exceedance.tenantId}
          // Seeds the picker so re-resolving edits the cited set instead of
          // rebuilding it from memory with nothing on screen to compare against.
          citedCorrectionIds={(detail?.manifestCorrections ?? []).map((c) => c.id)}
          onResolved={() => {
            setMarkResolvedOpen(false);
            onResolved();
          }}
          onCorrectionCreated={() => setMarkResolvedOpen(false)}
        />
      )}
    </>
  );
}
